import React from 'react'
import { useTranslation } from 'react-i18next'
import { Gem, Loader2, Trophy, User, XCircle } from 'lucide-react'
import { formatPrice, parseSkinName } from '../../../utils/formatters'
import ProfileLink from '../../../components/ProfileLink'
import TeamTag from '../../../components/TeamTag'
import useAppStore from '../../../store/useAppStore'

/**
 * Финал эвента Rush Drop (state = finished).
 * Четыре штатные концовки:
 * 1. winners пуст + participants > 0 → «Подводим итоги…» (розыгрыш идёт с буфером 3–4 минуты);
 * 2. winners пуст + participants = 0 → «Никто не справился — призы сгорели»;
 * 3. winners короче prize_places → занятые места + серые «Место не разыграно»;
 * 4. все места разыграны → обычная таблица победителей.
 * Факт выигрыша и приз берём из you.won / you.place / you.prize.
 * Своя строка в winners помечена флагом you === 1. you.rank — место по баллам,
 * к призам отношения не имеет, на этом экране не показываем.
 */
const RushDropWinners = ({ winners, participants, prizePlaces, you }) => {
  const { t } = useTranslation()
  const isGuest = useAppStore((state) => state.isGuest)
  const openLogin = useAppStore((state) => state.openLogin)

  const list = Array.isArray(winners) ? winners : []
  const participantsCount = Number(participants || 0)
  const placesTotal = Number(prizePlaces || 0)

  // Итоги ещё считаются (воркер догоняет хвост раундовых игр)
  if (list.length === 0 && participantsCount > 0) {
    return (
      <section className="rounded-2xl border border-white/10 bg-surface p-6">
        <div className="flex min-h-[160px] flex-col items-center justify-center gap-3 text-center">
          <Loader2 size={28} className="animate-spin text-accent-primary" />
          <p className="font-semibold text-white">
            {t('rushdrop.winners.calculating', 'Подводим итоги…')}
          </p>
          <p className="text-xs text-text-secondary">
            {t('rushdrop.winners.calculatingHint', 'Это займёт пару минут — страница обновится сама')}
          </p>
        </div>
      </section>
    )
  }

  // Никто не выполнил задания — штатный финал, не ошибка
  if (list.length === 0 && participantsCount === 0) {
    return (
      <section className="rounded-2xl border border-white/10 bg-surface p-6">
        <div className="flex min-h-[120px] flex-col items-center justify-center gap-3 text-center">
          <XCircle size={28} className="text-text-secondary" />
          <p className="font-semibold text-white">
            {t('rushdrop.winners.burned', 'Никто не справился — призы сгорели')}
          </p>
          <p className="text-xs text-text-secondary">
            {t('rushdrop.winners.burnedHint', 'Следующий Rush Drop скоро')}
          </p>
        </div>
      </section>
    )
  }

  const sorted = [...list].sort((a, b) => Number(a?.place || 0) - Number(b?.place || 0))
  const takenPlaces = new Set(sorted.map((w) => Number(w?.place)))
  const emptyPlaces = []
  for (let place = 1; place <= placesTotal; place += 1) {
    if (!takenPlaces.has(place)) {
      emptyPlaces.push(place)
    }
  }

  // Свой результат: факт выигрыша и приз — из you.won / you.prize,
  // призовое место — из you.place. you.rank (место по баллам) здесь не используем.
  const won = Boolean(you?.won)
  const qualified = Boolean(you?.qualified)

  const renderPrize = (prize) => {
    if (prize?.kind === 'skin' && prize?.skin) {
      const skin = parseSkinName(prize.skin.name)
      return (
        <span className="flex min-w-0 items-center gap-2">
          {prize.skin.image && (
            <img
              src={prize.skin.image}
              alt={prize.skin.name || ''}
              className="h-8 w-8 shrink-0 object-contain"
              loading="lazy"
            />
          )}
          <span className="truncate text-xs font-medium text-white">
            {skin.title || prize.skin.name}
          </span>
        </span>
      )
    }
    return (
      <span className="flex items-center gap-1 text-sm font-bold tabular-nums text-accent-primary">
        <Gem size={14} />
        {formatPrice(prize?.crystals)}
      </span>
    )
  }

  return (
    <section>
      <h2 className="mb-3 flex items-center gap-2 text-lg font-bold text-white">
        <Trophy size={18} className="text-accent-primary" />
        {t('rushdrop.winners.title', 'Победители')}
      </h2>

      {/* Свой результат */}
      <div
        className={`mb-3 rounded-2xl border p-4 ${
          won
            ? 'border-accent-primary/40 bg-accent-primary/10'
            : 'border-white/10 bg-surface'
        }`}
      >
        {isGuest ? (
          <button type="button" onClick={openLogin} className="rounded-xl bg-accent-primary px-4 py-2.5 text-sm font-bold text-dark-primary">{t('guest.login')}</button>
        ) : won ? (
          <div className="flex items-center justify-between gap-3">
            <span className="font-bold text-accent-primary">
              {t('rushdrop.winners.youWon', 'Ты выиграл!')}
            </span>
            {renderPrize(you?.prize)}
          </div>
        ) : qualified ? (
          <p className="text-sm text-text-secondary">
            {t('rushdrop.winners.youNoPrize', 'Ты участвовал, приз не достался')}
          </p>
        ) : (
          <p className="text-sm text-text-secondary">
            {t('rushdrop.winners.notParticipated', 'Задания не выполнены — ты не в розыгрыше')}
          </p>
        )}
      </div>

      <div className="space-y-2">
        {sorted.map((winner) => (
          <div
            key={winner.place}
            className={`flex items-center gap-3 rounded-xl border px-3 py-2 ${
              Number(winner?.you) === 1
                ? 'border-accent-primary/40 bg-accent-primary/10'
                : 'border-white/5 bg-white/[0.02]'
            }`}
          >
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-xs font-bold tabular-nums text-white">
              {winner.place}
            </span>
            {/* Аватар и имя победителя кликабельны: slug === null → не кликабелен */}
            <ProfileLink slug={winner.slug} className="shrink-0">
              {winner?.avatar ? (
                <img
                  src={winner.avatar}
                  alt=""
                  className="h-8 w-8 rounded-full border border-white/15 object-cover"
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none'
                  }}
                />
              ) : (
                <div className="flex h-8 w-8 items-center justify-center rounded-full border border-white/10 bg-white/5">
                  <User size={14} className="text-text-secondary" />
                </div>
              )}
            </ProfileLink>
            <p className="min-w-0 flex-1 truncate text-sm font-medium text-white">
              <TeamTag team={winner?.team} className="" />
              <ProfileLink slug={winner.slug}>
                {winner?.name || t('rushdrop.leaderboard.player', 'Игрок')}
              </ProfileLink>
            </p>
            {renderPrize(winner)}
          </div>
        ))}

        {/* Свободные места — участников оказалось меньше, чем призовых мест */}
        {emptyPlaces.map((place) => (
          <div
            key={`empty-${place}`}
            className="flex items-center gap-3 rounded-xl border border-white/5 bg-white/[0.02] px-3 py-2 opacity-40"
          >
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-xs font-bold tabular-nums text-text-secondary">
              {place}
            </span>
            <span className="flex-1 text-sm text-text-secondary">
              {t('rushdrop.winners.notDrawn', 'Место не разыграно')}
            </span>
          </div>
        ))}
      </div>
    </section>
  )
}

export default RushDropWinners
